import { useCallback, useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import type { PublicShareView, SourcePlaybackResponse } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button/Button';
import Form from '@carbon/react/es/components/Form/Form';
import { Stack } from '@carbon/react/es/components/Stack/Stack';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { ApiError, apiFetch } from '../api/client.js';

/**
 * Page publique d'un partage : aucun compte requis, seul le jeton de l'URL fait foi.
 *
 * Volontairement autonome et pauvre : elle n'affiche que ce que le lien expose (FR-34). Un lien
 * invalide, expiré ou révoqué renvoie 404 côté API — on présente un message neutre, sans
 * confirmer ce qui a échoué.
 */
export function SharePage() {
  const { token } = useParams({ strict: false }) as { token: string };

  const [view, setView] = useState<PublicShareView | null>(null);
  const [sources, setSources] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'loading' | 'password' | 'ready' | 'error'>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    async (pwd?: string) => {
      try {
        const resolved = await apiFetch<PublicShareView>(`/public/shares/${token}`, {
          method: 'POST',
          body: { password: pwd },
        });
        setView(resolved);
        setStatus('ready');

        // Les URLs de lecture ne figurent pas dans la vue (FR-34) : on les demande séparément.
        const entries = await Promise.all(
          resolved.videos.map(async (video) => {
            try {
              const source = await apiFetch<SourcePlaybackResponse>(
                `/public/shares/${token}/videos/${video.id}/source`,
                { method: 'POST', body: { password: pwd } },
              );
              return [video.id, source.url] as const;
            } catch {
              return null;
            }
          }),
        );
        setSources(Object.fromEntries(entries.filter((entry) => entry !== null)));
      } catch (error) {
        // 401 → le lien est protégé par mot de passe : on invite à le saisir sans rien révéler.
        if (error instanceof ApiError && error.status === 401) {
          setStatus('password');
          setMessage(pwd ? 'Mot de passe incorrect.' : null);
          return;
        }
        setStatus('error');
        setMessage('Ce lien de partage est invalide, expiré ou révoqué.');
      }
    },
    [token],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus('loading');
    await load(password);
    setSubmitting(false);
  };

  if (status === 'loading') {
    return (
      <main className="ddp-share">
        <Loading withOverlay={false} description="Chargement…" />
      </main>
    );
  }

  if (status === 'password') {
    return (
      <main className="ddp-share ddp-share--centered">
        <section className="ddp-share__gate">
          <h1>Contenu protégé</h1>
          <p>Ce partage est protégé par un mot de passe.</p>
          <Form onSubmit={onSubmitPassword}>
            <Stack gap={5}>
              {message && (
                <InlineNotification kind="error" title={message} lowContrast hideCloseButton />
              )}
              <TextInput
                id="share-password"
                type="password"
                labelText="Mot de passe"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <Button type="submit" disabled={submitting || password.length === 0}>
                Accéder
              </Button>
            </Stack>
          </Form>
        </section>
      </main>
    );
  }

  if (status === 'error' || !view) {
    return (
      <main className="ddp-share ddp-share--centered">
        <InlineNotification
          kind="error"
          title="Lien indisponible"
          subtitle={message ?? 'Ce lien de partage est invalide, expiré ou révoqué.'}
          lowContrast
          hideCloseButton
        />
      </main>
    );
  }

  return (
    <main className="ddp-share">
      <header className="ddp-share__header">
        <span className="ddp-share__brand">Drone Data Platform</span>
        <h1>{view.title}</h1>
        <p className="ddp-share__expiry">Accès valable jusqu’au {formatDate(view.expiresAt)}</p>
      </header>

      <div className="ddp-share__videos">
        {view.videos.map((video) => (
          <article key={video.id} className="ddp-share__video">
            {sources[video.id] ? (
              <video
                className="ddp-share__player"
                src={sources[video.id]}
                poster={video.coverUrl ?? undefined}
                controls
                preload="metadata"
                controlsList={view.allowDownload ? undefined : 'nodownload'}
              />
            ) : (
              <div className="ddp-share__player ddp-share__player--empty">
                Lecture indisponible pour cette vidéo.
              </div>
            )}
            <h2 className="ddp-share__video-title">{video.title}</h2>
          </article>
        ))}

        {view.images.map((image) => (
          <article key={image.id} className="ddp-share__video">
            <img className="ddp-share__player" src={image.url} alt="" />
          </article>
        ))}
      </div>
    </main>
  );
}

function formatDate(value: Date): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}
